import { useTranslation } from 'react-i18next'
import { FieldArray, useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { FILE_SCOPE_FLAT } from '../../../../constants/file'
import { SIZE } from '../../../../constants/size'
import { Field } from '../../../Field'
import { FileList, FilePreview, useFileUpload } from '../../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../../FilesPreviewGallery'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { Content } from '../../../Modal'
import {
  FLAT_FORM_FIELD_NAME_CONTRACTS,
  FLAT_FORM_INHABITANT_INITIAL_VALUES,
} from '../FlatForm.constants'
import { checkFlatFormForEmptyRequired } from '../FlatForm.utils'
import { FlatFormAdditionalFields } from '../FlatFormAdditionalFields'
import { FlatFormInhabitantFields } from '../FlatFormInhabitantFields'

import { BuildingField } from './BuildingField'
import { EntranceField } from './EntranceField'
import { FlatNumberField } from './FlatNumberField'
import { FloorNumberField } from './FloorNumberField'

import * as styles from './FlatFormFormFields.module.css'

function FlatFormFormFields({
  ContentComponent = Content,
  buildingSelectApiParams,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  files,
  notifications,
  setFiles,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { t } = useTranslation()

  const { dirty, isSubmitting } = useFormikContext()

  const {
    filesError,
    filesUploading,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onSetFiles: setFiles,
  })

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files })

  useFormValidationErrorsNotification({
    checkFormikFormEmptyRequired: checkFlatFormForEmptyRequired,
    files: { filesError, filesUploading },
    onRemoveNotification,
    onSetNotification,
  })

  function handleCancel() {
    return onCancel({ dirty, files })
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        disabled={isSubmitting}
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleRemoveFile}
      />
    )
  }

  const showFiles =
    files?.length > 0 || filesUploading?.length > 0 || filesError?.length > 0

  return (
    <FieldArray name={FLAT_FORM_FIELD_NAME_CONTRACTS} validateOnChange={false}>
      {({ push, remove }) => (
        <FormBody
          actionsProps={{
            ButtonClear: false,
            addProps: {
              disabled: isSubmitting,
              title: 'Modal.AddInhabitantBtnKey',
              onClick: () => push(FLAT_FORM_INHABITANT_INITIAL_VALUES),
            },
            cancelProps: {
              onClick: handleCancel,
            },
            className: styles.actions,
            isSubmitting,
            submitProps: {
              isLoading: isSubmitting,
            },
            uploadProps: {
              onFinishUpload: handleUploadFile,
              onStartUpload: handleStartUploading,
              onUploadErrors: handleUploadErrors,
            },
          }}
        >
          <ContentComponent {...contentComponentProps} size={contentSize}>
            <FormInlineNotifications notifications={notifications} />

            <FormFieldsContainer className={styles.fieldsContainer}>
              <BuildingField apiParams={buildingSelectApiParams} />
              <FlatNumberField />
              <EntranceField />
              <FloorNumberField />

              <FlatFormAdditionalFields />

              <FlatFormInhabitantFields
                disabled={isSubmitting}
                onRemove={remove}
              />
              {showFiles ? (
                <Field
                  shouldAdaptToMobileScreen
                  disabled={isSubmitting}
                  label={`${t('Flat.AttachLabelKey')}:`}
                >
                  <FileList
                    errorItems={filesError}
                    files={files}
                    renderItem={renderFile}
                    uploadingItems={filesUploading}
                    onRemoveFile={handleRemoveFile}
                  />
                </Field>
              ) : null}
            </FormFieldsContainer>
          </ContentComponent>
          <FilesPreviewGallery
            files={files}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_FLAT}
            onClose={handleCloseFilePreview}
          />
        </FormBody>
      )}
    </FieldArray>
  )
}

export default FlatFormFormFields
